import {InternalAdapterAPI} from '../adapters/internal/InternalAdapterAPI';
import {AD_TYPE} from '../enums/AdType';
import {ErrorCode} from '../enums/ErrorCode';
import {PlayerSize} from '../enums/PlayerSize';
import {VideoStartFailedReason} from '../enums/VideoStartFailedReason';
import {ErrorDetailTrackingSettingsProvider} from '../features/errordetails/ErrorDetailTracking';
import {OnErrorDetailEventObject} from '../features/errordetails/OnErrorDetailEventObject';
import {FeatureManager} from '../features/FeatureManager';
import {AdSample} from '../types/AdSample';
import type {AnalyticsConfig, AnalyticsError} from '../types/AnalyticsConfig';
import {AuthenticationCallback} from '../types/AuthenticationCallback';
import {CustomDataValues, extractCustomDataFieldsOnly, hasAnyCustomDataChanged} from '../types/CustomDataValues';
import type {QualityChangeEvent} from '../types/EventData';
import * as EventData from '../types/EventData';
import {FeatureConfigContainer} from '../types/FeatureConfigContainer';
import {PlaybackInfo} from '../types/PlaybackInfo';
import {Sample} from '../types/Sample';
import type {SourceMetadata} from '../types/SourceMetadata';
import Timespan from '../types/Timespan';
import {
  applySourceMetadataToAnalyticsConfig,
  getDeviceInformationFromAnalyticsConfig,
  getDomainFromAnalyticsConfig,
  guardAgainstDuplicatedUserId,
  guardConfigChangeAgainstMissingIsLive,
  guardConfigChangeAgainstMissingTitle,
  mergeAnalyticsConfig,
} from '../utils/analyticsConfigUtils';
import {CodecHelper} from '../utils/CodecHelper';
import {globalScope} from '../utils/globalScope';
import {
  getGlobalNavigatorLanguage,
  getGlobalNavigatorUserAgent,
  getGlobalScreenDimensions,
  hasGlobalWebOSApi,
} from '../utils/globalScopeUtils';
import {logger} from '../utils/Logger';
import {isFiniteNumber, isNumber} from '../utils/numberUtils';
import {clearSampleStateValues, getDefaultSamplePath} from '../utils/sampleUtils';
import {isBlank, isValidString} from '../utils/stringUtils';
import * as Utils from '../utils/Utils';
import {isErrorSeverity, isNotNullish, isNullish, tryJSONStringify} from '../utils/Utils';
import {generateUUID} from '../utils/uuidUtils';
import {VERSION} from '../utils/Version';
import {getWebOSTVModelName} from '../utils/WebOSUtils';

import {AdAnalytics} from './AdAnalytics';
import {Backend} from './Backend';
import {BackendFactory} from './BackendFactory';
import {EventDispatcher, Subscribable} from './EventDispatcher';
import {IdenticalErrorReportingLimiter} from './IdenticalErrorReportingLimiter';
import {NoOpBackend} from './NoOpBackend';
import {SessionPersistenceHandler} from './SessionPersistenceHandler';

export class Analytics {
  get version(): string {
    return VERSION;
  }

  static version: string = VERSION;

  private readonly adAnalytics: AdAnalytics | undefined;

  sample: Sample;

  backend!: Backend;
  readonly errorDetailTrackingSettingsProvider: ErrorDetailTrackingSettingsProvider;

  private config: AnalyticsConfig;
  private droppedSampleFrames = 0;

  private readonly authenticationCallback: AuthenticationCallback = {
    authenticationCompleted: (success: boolean, featureConfigs?: FeatureConfigContainer) => {
      this.featureManager.configureFeatures(success, featureConfigs);
    },
  };

  // used only in tests to inject mocks
  constructor(
    initialAnalyticsConfig: AnalyticsConfig,
    private readonly adapter: InternalAdapterAPI,
    private readonly backendFactory: BackendFactory,
    private readonly sessionHandler: SessionPersistenceHandler,
    private readonly featureManager: FeatureManager<FeatureConfigContainer>,
    private readonly onErrorDetailEventDispatcher: EventDispatcher<OnErrorDetailEventObject>,
    private readonly identicalErrorReportingLimiter: IdenticalErrorReportingLimiter,
  ) {
    this.config = initialAnalyticsConfig;
    this.sessionHandler = new SessionPersistenceHandler(this.config);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    this.errorDetailTrackingSettingsProvider = {
      get domain() {
        return getDomainFromAnalyticsConfig(that.config);
      },
      get licenseKey() {
        return that.config.key ?? '';
      },
      get impressionId() {
        return that.getCurrentImpressionId() ?? '';
      },
      get collectorConfig() {
        return that.config.config;
      },
    };

    const licenseKeyReceivedUnsubscriber = this.adapter.onLicenseKeyReceived.subscribe(
      (eventArgs: {licenseKey: string}) => {
        if (!this.config.key) {
          this.config.key = eventArgs.licenseKey;
        }
        licenseKeyReceivedUnsubscriber();
      },
    );

    this.sample = this.buildInitialSample();
    this.droppedSampleFrames = 0;
    this.init();
    this.setupStateMachineCallbacks();

    // initialize adapter (start to listen to the player events and call the callbacks accordingly)
    const features = this.adapter.initialize(this);
    this.featureManager.registerFeatures(features);

    if (this.adapter.adModule) {
      // this needs to be called after the adapter has been initialized and has created adModule (if any)
      this.adAnalytics = new AdAnalytics(this, this.adapter.adModule);
    }
  }

  get errorDetailSubscribable(): Subscribable<OnErrorDetailEventObject> {
    return this.onErrorDetailEventDispatcher;
  }

  init() {
    if (
      this.adapter.supportsDeferredLicenseLoading !== true &&
      (!isValidString(this.config.key) || isBlank(this.config.key))
    ) {
      logger.errorMessageToUser('Invalid analytics license key provided');
      return;
    }

    logger.initialize(this.config.debug);

    this.featureManager.resetFeatures();

    const domain = getDomainFromAnalyticsConfig(this.config);
    this.backend = this.backendFactory.createBackend(
      this.config,
      {key: this.config.key, domain, version: VERSION},
      this.adapter,
      this.authenticationCallback,
    );

    this.identicalErrorReportingLimiter.reset();

    this.setConfigParameters();
    this.setAutoDetectedDeviceInformation();
    this.sample.impressionId = generateUUID();
    this.sample.userId = this.sessionHandler.userId;

    if (this.adapter.videoCompletionTracker) {
      this.adapter.videoCompletionTracker.reset();
    }

    this.adapter.qualityChangeService.resetValues();
    this.adapter.ssaiService.reset();
  }

  setConfigParameters() {
    this.sample.key = this.config.key;
    this.sample.playerKey = this.config.playerKey;
    if (this.config.player) {
      this.sample.player = this.config.player;
    }
    this.sample.domain = getDomainFromAnalyticsConfig(this.config);

    this.sample.deviceInformation = getDeviceInformationFromAnalyticsConfig(this.config);

    this.sample.isLive = isNotNullish(this.config.isLive) ? this.config.isLive : false;

    this.sample.cdnProvider = this.config.cdnProvider;
    this.sample.videoId = this.config.videoId;
    this.sample.videoTitle = this.config.title;
    this.sample.customUserId = this.config.userId ?? this.config.customUserId;

    Utils.transferCustomDataFields(this.config, this.sample);

    this.sample.experimentName = this.config.experimentName;
  }

  setAutoDetectedDeviceInformation(): void {
    // do not overwrite existing model value (set through config)
    if (isNotNullish(this.sample.deviceInformation?.model)) {
      return;
    }

    if (!hasGlobalWebOSApi()) return;

    const modelName = getWebOSTVModelName();
    if (isNullish(modelName)) return;

    this.sample.deviceInformation = this.sample.deviceInformation ?? {};
    this.sample.deviceInformation.model = modelName;
  }

  private setupStateMachineCallbacks() {
    // All of these are called in the onLeaveState Method.
    // So it's the last sample
    this.adapter.stateMachineCallbacks.setup = () => {
      logger.log(
        'Setup bitmovin analytics ' + this.sample.analyticsVersion + ' with impressionId: ' + this.sample.impressionId,
      );

      // Call sendSample with isSetup option to not send a sample but just populate the sample (playback info,
      // quality info, etc.). This is necessary so that fields like videoWindowHeight are available for CSAI pre-roll
      // ad samples before the first real sendSample() call after startup sample was sent
      this.sendSample({isSetup: true});
    };

    this.adapter.stateMachineCallbacks.startup = (duration: number, state: string) => {
      this.setState(state);
      this.sample.supportedVideoCodecs = CodecHelper.supportedVideoFormats;
      // videoStartupTime has to be > 0 on startup sample
      // it is used to mark a successfull impression and billing depends on it
      const sanitizeVideoStartupTime = Math.max(duration, 1);
      this.setDuration(sanitizeVideoStartupTime);

      this.sample.videoStartupTime = sanitizeVideoStartupTime;
      // set to fixed 1ms, because this field is deprecated and the metric that was using it does not exist anymore
      this.sample.playerStartupTime = 1;
      this.sample.startupTime = sanitizeVideoStartupTime + this.sample.playerStartupTime;

      this.sample.autoplay = this.adapter.getAutoPlay();

      const drmPerformance = this.adapter.getDrmPerformanceInfo();
      if (isNotNullish(drmPerformance)) {
        this.sample.drmType = drmPerformance.drmType;
        this.sample.drmLoadTime = drmPerformance.drmLoadTime;
      }

      const {videoBitrate, audioBitrate} = this.adapter.getCurrentPlaybackInfo();
      // set the flag to allow quality change events after startup and try to set the startup bitrate values (if any)
      // as the initial ones, to be able to compare them with future quality changes
      this.adapter.qualityChangeService.setStartupHasFinished(videoBitrate, audioBitrate);

      const navigationStart = globalScope.performance?.timing?.navigationStart;
      if (isNotNullish(navigationStart) && isFiniteNumber(navigationStart)) {
        // round the final time to a whole number to avoid unnecessary precision (performance API returns a high-precision timestamp as double)
        this.sample.pageLoadTime = Math.round(Date.now() - globalScope.performance.timing.navigationStart);
      }

      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.programChange = (sourceMetadata: SourceMetadata) => {
      // apply source metadata to config
      this.config = applySourceMetadataToAnalyticsConfig(this.config, sourceMetadata);

      // reset sample to init values
      this.sample = this.buildInitialSample();
      // apply a source metadata path to the sample (if any)
      this.sample.path = isValidString(sourceMetadata.path) ? sourceMetadata.path : getDefaultSamplePath();

      // reinitialize to finish reset of the Analytics module
      this.init();

      // build startup "programchange" sample
      this.setState('programchange');
      this.sample.isProgramChange = true;
      this.sample.supportedVideoCodecs = CodecHelper.supportedVideoFormats;
      // videoStartupTime has to be > 0 on the startup sample
      // it is used to mark a successful impression and billing depends on it
      const programChangeVideoStartupTime = 1;
      this.setDuration(programChangeVideoStartupTime);
      this.sample.videoStartupTime = programChangeVideoStartupTime;
      this.sample.startupTime = programChangeVideoStartupTime; // is not affected now by player startup time

      this.sample.autoplay = this.adapter.getAutoPlay();

      // DRM load time is only important when we load the source, DRM request does not happen between active playback
      // so it can't happen during the program change
      this.sample.drmType = undefined;
      this.sample.drmLoadTime = undefined;

      const {videoBitrate, audioBitrate} = this.adapter.getCurrentPlaybackInfo();
      // set the flag to allow quality change events after startup and try to set the startup bitrate values (if any)
      // as the initial ones, to be able to compare them with future quality changes
      this.adapter.qualityChangeService.setStartupHasFinished(videoBitrate, audioBitrate);

      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.updateSourceMetadata = (sourceMetadata: SourceMetadata) => {
      // apply source metadata to config
      this.config = applySourceMetadataToAnalyticsConfig(this.config, sourceMetadata);
      // new config should be immediately applied to the current sample
      this.setConfigParameters();
      this.setAutoDetectedDeviceInformation();
      // apply a source metadata path to the sample (if any)
      this.sample.path = isValidString(sourceMetadata.path) ? sourceMetadata.path : getDefaultSamplePath();
    };

    this.adapter.stateMachineCallbacks.playing = (duration: number, state: string) => {
      this.setDuration(duration);
      this.setState(state);
      this.sample.played = duration;
      this.setCompletionValues();
      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.closedAd = (duration) => {
      //set ad properties
      this.setDuration(duration);
      this.setState('ad');
      this.sample.ad = AD_TYPE.CSAI;
      this.sendSample({isClosing: true});
      clearSampleStateValues(this.sample);
    };

    this.adapter.stateMachineCallbacks.closedPlaying = (duration: number) => {
      this.setDuration(duration);
      this.setState('playing');
      this.sample.played = duration;
      this.setCompletionValues();

      this.sendSample({isClosing: true});
      clearSampleStateValues(this.sample);
    };

    this.adapter.stateMachineCallbacks.heartbeat = (duration, state, heartbeatPayload) => {
      this.setState(state);
      this.setDuration(duration);

      this.sample = {
        ...this.sample,
        ...heartbeatPayload,
      };

      if (state === 'playing') {
        this.setCompletionValues();
      }

      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.qualitychange = (event: QualityChangeEvent) => {
      this.adapter.qualityChangeService.startResetInterval();
      this.adapter.qualityChangeService.increaseCounter();

      if (this.adapter.qualityChangeService.hasExceededQualityChangeThreshold()) {
        this.adapter.stateMachineCallbacks.error({
          code: ErrorCode.QUALITY_CHANGE_THRESHOLD_EXCEEDED.code,
          message: ErrorCode.QUALITY_CHANGE_THRESHOLD_EXCEEDED.message,
          errorSeverity: ErrorCode.QUALITY_CHANGE_THRESHOLD_EXCEEDED.errorSeverity,
          legacyData: undefined,
          data: {},
          currentTime: event.currentTime,
        });
        return;
      }

      this.setState('qualitychange');
      this.setDuration(0);
      this.adapter.stateMachineCallbacks.setVideoTimeEndFromEvent(event);
      this.adapter.stateMachineCallbacks.setVideoTimeStartFromEvent(event);
      this.adapter.stateMachineCallbacks.updateSampleQualityInfo(event);

      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.updateSampleQualityInfo = (event: QualityChangeEvent) => {
      if (event.type === 'audio') {
        if (isNumber(event.bitrate)) {
          this.sample.audioBitrate = event.bitrate;
        }
        if (isValidString(event.codec)) {
          this.sample.audioCodec = event.codec;
        }
      } else if (event.type === 'video') {
        if (isNumber(event.bitrate)) {
          this.sample.videoBitrate = event.bitrate;
        }
        if (isValidString(event.codec)) {
          this.sample.videoCodec = event.codec;
        }
        if (isNumber(event.height)) {
          this.sample.videoPlaybackHeight = event.height;
        }
        if (isNumber(event.width)) {
          this.sample.videoPlaybackWidth = event.width;
        }
      }
    };

    this.adapter.stateMachineCallbacks.audiotrack_changing = () => {
      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.pause = (duration: number, state: string) => {
      this.setDuration(duration);
      this.setState(state);

      this.sample.paused = duration;

      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.seeking = (duration: number) => {
      this.setDuration(duration);
      this.setState('seeking');
      this.sample.seeked = duration;
      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.buffering = (duration: number, state: string) => {
      this.setDuration(duration);
      this.setState(state);

      this.sample.buffered = duration;

      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.videoStartFailed = (duration, reason) => {
      this.setState('startup');
      this.sample.videoStartFailed = true;
      this.sample.videoStartFailedReason = reason;
      this.sample.duration = duration;

      if (reason === VideoStartFailedReason.PLAYER_ERROR) {
        // on player error we should not send a video startup sample
        return;
      }

      if (reason === VideoStartFailedReason.TIMEOUT) {
        this.sample.errorCode = ErrorCode.VIDEO_STARTUP_TIMEOUT_REACHED.code;
        this.sample.errorMessage = ErrorCode.VIDEO_STARTUP_TIMEOUT_REACHED.message;
        this.sample.errorSeverity = ErrorCode.VIDEO_STARTUP_TIMEOUT_REACHED.errorSeverity;

        this.onErrorDetailEventDispatcher.dispatch({
          code: ErrorCode.VIDEO_STARTUP_TIMEOUT_REACHED.code,
          message: ErrorCode.VIDEO_STARTUP_TIMEOUT_REACHED.message,
          severity: ErrorCode.VIDEO_STARTUP_TIMEOUT_REACHED.errorSeverity,
        });
      }

      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.error = (event: Readonly<EventData.ErrorEvent>) => {
      const error = this.transformError(
        {code: event.code, message: event.message, severity: event.errorSeverity ?? 'CRITICAL'},
        event.originalError,
      );

      if (error.code && !this.identicalErrorReportingLimiter.shouldReportError(error.code, error.severity)) {
        logger.log(
          `Bitmovin Analytics: Processing of the error with code ${error.code} was ignored because the error limit was exceeded.`,
        );
        return;
      }

      this.adapter.stateMachineCallbacks.setVideoTimeEndFromEvent(event);
      this.adapter.stateMachineCallbacks.setVideoTimeStartFromEvent(event);

      this.setState('error');
      this.setDuration(0);
      this.sample.errorCode = error.code;
      this.sample.errorMessage = error.message;
      this.sample.errorSeverity = error.severity;
      this.sample.errorData = tryJSONStringify(event.legacyData);
      this.sample.errorSegments = this.adapter.getSegmentInfo().map((s) => s.name);

      if (this.adapter.onError) {
        this.adapter.onError();
      }

      this.onErrorDetailEventDispatcher.dispatch({
        code: error.code,
        message: error.message,
        severity: error.severity,
        errorData: event.data,
      });

      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.ad = (duration: number, state: string) => {
      this.setDuration(duration);
      this.setState(state);
      this.sample.ad = AD_TYPE.CSAI;
      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.mute = () => {
      this.sample.isMuted = true;
    };

    this.adapter.stateMachineCallbacks.unMute = () => {
      this.sample.isMuted = false;
    };

    this.adapter.stateMachineCallbacks.subtitle_changing = () => {
      this.sendSample();
    };

    this.adapter.stateMachineCallbacks.setVideoTimeEndFromEvent = (analyticsEventBase) => {
      // if we don't have the current playback time, skip and do nothing
      if (!isNumber(analyticsEventBase?.currentTime)) {
        return;
      }

      this.setVideoTimeEnd(Utils.calculateTime(analyticsEventBase.currentTime));
    };

    this.adapter.stateMachineCallbacks.setVideoTimeStartFromEvent = (analyticsEventBase) => {
      // if we don't have the current playback time, skip and do nothing
      if (!isNumber(analyticsEventBase?.currentTime)) {
        return;
      }

      this.setVideoTimeStart(Utils.calculateTime(analyticsEventBase.currentTime));
    };

    this.adapter.stateMachineCallbacks.manualSourceChange = (event: {config: AnalyticsConfig}) => {
      this.adapter.resetSourceRelatedState();
      this.sample = this.buildInitialSample();
      this.droppedSampleFrames = 0;
      this.config = Object.keys(event.config).length > 0 ? event.config : this.config;
      this.init();
    };

    this.adapter.stateMachineCallbacks.initialSourceChange = (event: {config: AnalyticsConfig}) => {
      this.config = event.config;
      this.setConfigParameters();
      this.setAutoDetectedDeviceInformation();
    };

    // The video has ended and we set up for a new impression
    this.adapter.stateMachineCallbacks.end = () => {
      // !!!! THIS IS OLD AND WRONG IMPLEMENTATION !!!!
      // (check deprecation comment on callback)
      this.sample = this.buildInitialSample();
      this.droppedSampleFrames = 0;
      this.init();
    };

    this.adapter.stateMachineCallbacks.release = () => {
      this.backend = new NoOpBackend();
      this.adAnalytics?.release();
      this.adapter.qualityChangeService.stopResetInterval();
      this.identicalErrorReportingLimiter.reset();
    };

    this.adapter.stateMachineCallbacks.customdatachange = (_duration, _state, eventData) => {
      if (isNotNullish(eventData?.customDataValues)) {
        this.changeCustomData(eventData.customDataValues);
      }
    };
  }

  sourceChange = (config: AnalyticsConfig | null | undefined) => {
    logger.log('Processing Source Change for Analytics', config);
    if (isNullish(config)) {
      this.adapter.sourceChange(this.config);
      return;
    }

    guardConfigChangeAgainstMissingTitle(this.config, config);
    guardConfigChangeAgainstMissingIsLive(this.config, config);
    guardAgainstDuplicatedUserId(config);

    const newConfig = mergeAnalyticsConfig(this.config, config);
    this.adapter.sourceChange(newConfig);
  };

  setCustomDataOnce = (values: CustomDataValues) => {
    // save old config to be able to return previous custom data values once the sample is sent
    const oldConfig = {...this.config};

    this.setState('customdatachange');

    this.sendSample({customDataOnceValues: values});
    // this method doesn't call changeCustomData() second time because in that method we have check for null values
    // to prevent overriding existing customData values with null if some field in object doesn't have value
    // because of that if some customData field had previous value null and it got changed to something else
    // it will not be possible to return it back to null using that method
    this.config = {...oldConfig};
    this.setConfigParameters();
    this.setAutoDetectedDeviceInformation();
  };

  isCustomDataChangeAllowed = (values: CustomDataValues): boolean => {
    return hasAnyCustomDataChanged(values, this.config);
  };

  getCurrentImpressionId = (): string | undefined => {
    return this.sample.impressionId;
  };

  getUserId = (): string => {
    return this.sessionHandler.userId;
  };

  setDuration(duration: number) {
    this.sample.duration = duration;
  }

  setState(state: string) {
    this.sample.state = state;
  }

  setPlaybackInfoFromAdapter() {
    const info = this.adapter.getCurrentPlaybackInfo();
    if (!info) {
      return;
    }

    // if set, we always apply `config.isLive` in favor of `info.isLive`
    this.sample.isLive = isNotNullish(this.config.isLive) ? this.config.isLive : info.isLive;
    // if `isLive` is still not defined, we set it to default `false` value
    if (isNullish(this.sample.isLive)) {
      this.sample.isLive = false;
    }

    if (isValidString(info.size)) {
      this.sample.size = info.size;
    }
    if (isValidString(info.playerTech)) {
      this.sample.playerTech = info.playerTech;
    }
    if (isNumber(info.videoDuration)) {
      this.sample.videoDuration = Utils.calculateTime(info.videoDuration || 0);
    }
    if (isValidString(info.streamFormat)) {
      this.sample.streamFormat = info.streamFormat;
    }
    if (isValidString(info.mpdUrl)) {
      this.sample.mpdUrl = info.mpdUrl;
    }
    if (isValidString(info.m3u8Url)) {
      this.sample.m3u8Url = info.m3u8Url;
    }
    if (isValidString(info.progUrl)) {
      this.sample.progUrl = info.progUrl;
    }
    if (isNumber(info.videoWindowWidth)) {
      this.sample.videoWindowWidth = info.videoWindowWidth;
    }
    if (isNumber(info.videoWindowHeight)) {
      this.sample.videoWindowHeight = info.videoWindowHeight;
    }
    if (isNumber(info.screenHeight)) {
      this.sample.screenHeight = info.screenHeight;
    }
    if (isNumber(info.screenWidth)) {
      this.sample.screenWidth = info.screenWidth;
    }
    if (Utils.isBoolean(info.isMuted)) {
      this.sample.isMuted = info.isMuted;
    }
    if (Utils.isBoolean(info.isCasting)) {
      this.sample.isCasting = info.isCasting;
    }
    if (isValidString(info.castTech)) {
      this.sample.castTech = info.castTech;
    }
    if (isValidString(info.videoTitle) && !this.config.title) {
      this.sample.videoTitle = info.videoTitle;
    }
    if (isValidString(info.audioCodec)) {
      this.sample.audioCodec = info.audioCodec;
    }
    if (isValidString(info.videoCodec)) {
      this.sample.videoCodec = info.videoCodec;
    }
    if (isValidString(info.audioLanguage)) {
      this.sample.audioLanguage = info.audioLanguage;
    }
    if (Utils.isBoolean(info.subtitleEnabled)) {
      this.sample.subtitleEnabled = info.subtitleEnabled;
    }
    if (isValidString(info.subtitleLanguage)) {
      this.sample.subtitleLanguage = info.subtitleLanguage;
    } else {
      this.sample.subtitleLanguage = undefined;
    }
    if (isNumber(info.droppedFrames)) {
      this.sample.droppedFrames = Math.max(info.droppedFrames - this.droppedSampleFrames, 0);
      this.droppedSampleFrames = info.droppedFrames;
    }
  }

  sendSample(options?: {customDataOnceValues?: CustomDataValues; isClosing?: boolean; isSetup?: boolean}) {
    // skip setting of player playback info in case of the "unload path" because at this moment
    // the player might be already destroyed and the adapter would not be able to get the info from it
    if (!options?.isClosing) {
      this.setPlaybackInfoFromAdapter();
    }

    this.sample.time = Date.now();

    // get current download speed info from the adapter and set it on the sample before sending it
    this.sample.downloadSpeedInfo = this.adapter.getDownloadSpeedInfo();
    // reset current download speed info measures in the adapter to start fresh for the next sample,
    // so we always have the info for the current sample only
    this.adapter.resetDownloadSpeedInfoMeasures();

    this.adapter.ssaiService.manipulate(this.sample);

    // if we have received custom data from the setCustomDataOnce API call, we need to set
    // them as "the last one" (we need to override normal customData and SSAI custom data)
    if (options?.customDataOnceValues != null) {
      this.changeCustomData(options.customDataOnceValues);
    }

    // Initial quality info should be available for the first sample after setup, so if there is no quality info yet, we try to set it *before* sending a sample.
    // In the general case, a qualitychange event first triggers a sample of the state we were in before the qualitychange sample itself is sent.
    // So to only report quality updates on the qualitychange sample and not the ones before it, we need to update the quality info *after* sending a sample,
    // making it available for future samples (e.g. qualitychange sample).
    // We only check videoBitrate and not audioBitrate since the latter is often reported as null/0 anyways.
    const hasInitialQualityInfo = isNotNullish(this.sample.videoBitrate) && this.sample.videoBitrate > 0;

    if (!options?.isClosing && !hasInitialQualityInfo) {
      setQualityInfoOnSample(this.sample, this.adapter.getCurrentPlaybackInfo());
    }

    // No sample should be sent for the setup callback
    if (options?.isSetup) {
      return;
    }

    // make a copy of sample to not pass it as a reference to avoid modifications
    // to the current "analytics sample state" from backend side-effects
    const copySample = {...this.sample};

    if (options?.isClosing) {
      this.backend.sendUnloadRequest(copySample);
    } else {
      this.backend.sendRequest(copySample);
    }

    if (!options?.isClosing && hasInitialQualityInfo) {
      setQualityInfoOnSample(this.sample, this.adapter.getCurrentPlaybackInfo());
    }

    // clear sample state values after sending a sample
    clearSampleStateValues(this.sample);
  }

  sendErrorSsaiAdSample(adSample: AdSample, originalError?: any): void {
    if (this.config.config?.ssaiEngagementTrackingEnabled !== true) {
      // if not enabled in config, do not send SSAI engagement AdSample
      return;
    }

    const transformed = this.transformError(
      {code: adSample.errorCode, message: adSample.errorMessage, severity: adSample.errorSeverity!},
      originalError,
    );
    this.backend.sendAdRequest({
      ...adSample,
      errorCode: transformed.code,
      errorMessage: transformed.message,
      errorSeverity: transformed.severity,
    });
  }

  sendQuartileSsaiAdSample(adSample: AdSample): void {
    if (this.config.config?.ssaiEngagementTrackingEnabled !== true) {
      // if not enabled in config, do not send SSAI engagement AdSample
      return;
    }

    this.backend.sendAdRequest(adSample);
  }

  sendUnloadSsaiAdSample(adSample: AdSample): void {
    if (this.config.config?.ssaiEngagementTrackingEnabled !== true) {
      // if not enabled in config, do not send SSAI engagement AdSample
      return;
    }

    this.backend.sendUnloadAdRequest(adSample);
  }

  /**
   * This method sanitizes the input and updates the analytics config.
   * @param values Object containing the customData and other fields
   */
  private changeCustomData = (values: CustomDataValues) => {
    this.config = {
      ...this.config,
      ...extractCustomDataFieldsOnly(values),
    };
    this.setConfigParameters();
    this.setAutoDetectedDeviceInformation();
  };

  private setCompletionValues() {
    if (this.adapter.videoCompletionTracker) {
      const completed = this.adapter.videoCompletionTracker.addWatched({
        start: this.sample.videoTimeStart,
        end: this.sample.videoTimeEnd,
      } as Timespan);
      const completedTotal = this.adapter.videoCompletionTracker.getCompletionPercentage();

      if (!Number.isNaN(completed) && !Number.isNaN(completedTotal)) {
        this.sample.completed = completed;
        this.sample.completedTotal = completedTotal;
      }
    }
  }

  private setVideoTimeStart(value: number | undefined) {
    this.sample.videoTimeStart = value;
  }

  private setVideoTimeEnd(value: number | undefined) {
    this.sample.videoTimeEnd = value;
  }

  transformError(error: AnalyticsError, originalError: any): AnalyticsError {
    const analyticsError = {
      code: error.code,
      message: error.message,
      severity: error.severity,
    };

    try {
      const transformedError = this.config.config?.errorTransformerCallback?.transform(analyticsError, {
        originalError: originalError,
      });

      analyticsError.code = isNumber(transformedError?.code) ? transformedError?.code : analyticsError.code;
      analyticsError.message = isValidString(transformedError?.message)
        ? transformedError?.message
        : analyticsError.message;
      analyticsError.severity =
        isValidString(transformedError?.severity) && isErrorSeverity(transformedError?.severity)
          ? transformedError!.severity
          : analyticsError.severity;
    } catch (error) {
      logger.error('Error in errorTransformerCallback', error);
    }

    return analyticsError;
  }

  private buildInitialSample(): Sample {
    const player = this.config.player || this.adapter.getPlayerName();
    const screenDimensions = getGlobalScreenDimensions();

    return {
      ad: 0,
      analyticsVersion: VERSION,
      audioBitrate: 0,
      buffered: 0,
      completedTotal: 0,
      droppedFrames: 0,
      duration: 0,
      isLive: this.config.isLive ?? false,
      language: getGlobalNavigatorLanguage(),
      pageLoadTime: 0,
      pageLoadType: Utils.getPageLoadType(),
      path: getDefaultSamplePath(),
      paused: 0,
      platform: 'web',
      played: 0,
      player,
      playerStartupTime: 0,
      playerTech: this.adapter.getPlayerTech(),
      screenHeight: screenDimensions?.height,
      screenWidth: screenDimensions?.width,
      seeked: 0,
      size: PlayerSize.Window,
      startupTime: 0,
      time: 0,
      userAgent: getGlobalNavigatorUserAgent(),
      version: player + '-' + this.adapter.getPlayerVersion(),
      videoBitrate: 0,
      videoDuration: 0,
      videoPlaybackHeight: 0,
      videoPlaybackWidth: 0,
      videoStartupTime: 0,
      videoTimeEnd: 0,
      videoTimeStart: 0,
      videoWindowHeight: 0,
      videoWindowWidth: 0,
    };
  }

  static create(config: AnalyticsConfig, adapter: InternalAdapterAPI): Analytics {
    const backendFactory: BackendFactory = new BackendFactory();
    const sessionHandler: SessionPersistenceHandler = new SessionPersistenceHandler(config);
    const featureManager = new FeatureManager<FeatureConfigContainer>();
    const onErrorDetailEventDispatcher = new EventDispatcher<OnErrorDetailEventObject>();
    const identicalErrorReportingLimiter = new IdenticalErrorReportingLimiter();
    return new Analytics(
      config,
      adapter,
      backendFactory,
      sessionHandler,
      featureManager,
      onErrorDetailEventDispatcher,
      identicalErrorReportingLimiter,
    );
  }
}

// We separate the quality info from `setPlaybackInfoFromAdapter` to be able to explicitly set/update the quality info only when it changes.
export function setQualityInfoOnSample(sample: Sample, playbackInfo: PlaybackInfo) {
  if (isNumber(playbackInfo.videoPlaybackHeight)) {
    sample.videoPlaybackHeight = playbackInfo.videoPlaybackHeight;
  }
  if (isNumber(playbackInfo.videoPlaybackWidth)) {
    sample.videoPlaybackWidth = playbackInfo.videoPlaybackWidth;
  }
  if (isNumber(playbackInfo.videoBitrate)) {
    sample.videoBitrate = playbackInfo.videoBitrate;
  }
  if (isNumber(playbackInfo.audioBitrate)) {
    sample.audioBitrate = playbackInfo.audioBitrate;
  }
}
